import { lazy, Suspense, useEffect } from 'react';
import { NavLink, Outlet, useMatches } from 'react-router';
import { create } from 'zustand';
import { useT } from '@/i18n/languageStore';
import { useOffersWatcher } from '@/features/help-exchange/useOffersWatcher';
import { useUnreadMessagesCount } from '@/features/messaging/queries';
import { useCallStore } from '@/state/callStore';
import { useConnectionStore } from '@/state/connectionStore';
import { useSessionStore } from '@/state/sessionStore';
import { selectPending, useWarningsStore } from '@/state/warningsStore';
import { Icon, type IconName } from '@/ui/kit/Icon';
import { useIsCompact } from '@/ui/layout/useMediaQuery';
import { Overlays } from '../overlays/Overlays';
import styles from './AppShell.module.css';

// Loaded only when needed, to keep the first download small (tasks.md T204).
const loadCallOverlay = () => import('@/features/calls/CallOverlay');
const CallOverlay = lazy(() => loadCallOverlay().then((m) => ({ default: m.CallOverlay })));
const WarningNotice = lazy(() =>
  import('../overlays/WarningNotice').then((m) => ({ default: m.WarningNotice })),
);
/** Fetched soon after start-up, so the first incoming call does not wait for a download. */
const PREFETCH_CALL_OVERLAY_MS = 3000;

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
}

/** The four sections of the Android bottom tabs (mobile/src/components/BottomTabBar.tsx), in the same order. */
const SECTIONS: NavItem[] = [
  { to: '/', label: 'Home', icon: 'home', end: true },
  { to: '/browse', label: 'Browse', icon: 'browse' },
  { to: '/messages', label: 'Messages', icon: 'messages' },
  { to: '/profile', label: 'Profile', icon: 'profile' },
];
const ADMIN: NavItem = { to: '/admin', label: 'Admin', icon: 'shield-check' };

/** Pages set their own title here; the shell adds the unread count and the app name. */
export const usePageTitle = create<{ title: string | null; set: (title: string | null) => void }>()(
  (set) => ({
    title: null,
    set: (title) => set({ title }),
  }),
);

export function useDocumentTitle(title: string | null) {
  useEffect(() => {
    usePageTitle.getState().set(title);
    return () => usePageTitle.getState().set(null);
  }, [title]);
}

function MainNav({
  layout,
  unread,
  newOffers,
  isAdmin,
}: {
  layout: 'bottom' | 'side';
  unread: number;
  /** Offers on the member's own posts they have not opened yet; shown on Browse, where the posts are. */
  newOffers: number;
  isAdmin: boolean;
}) {
  const t = useT();
  const items = layout === 'side' && isAdmin ? [...SECTIONS, ADMIN] : SECTIONS;
  return (
    <nav aria-label={t('Main')} className={styles.nav} data-layout={layout}>
      {layout === 'side' ? (
        <span className={styles.brand} aria-hidden="true">
          {t('Kindra')}
        </span>
      ) : null}
      <ul className={styles.navList}>
        {items.map((item) => {
          const count = item.to === '/messages' ? unread : item.to === '/browse' ? newOffers : 0;
          const meaning =
            item.to === '/messages' ? 'unread' : newOffers === 1 ? 'new offer' : 'new offers';
          return (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={styles.navLink}
                aria-label={count > 0 ? `${t(item.label)}, ${count} ${t(meaning)}` : undefined}
              >
                <span className={styles.navIcon}>
                  <Icon name={item.icon} size={22} />
                  {count > 0 ? (
                    <span className={styles.badge} aria-hidden="true">
                      {count > 99 ? '99+' : count}
                    </span>
                  ) : null}
                </span>
                <span className={styles.navLabel}>{t(item.label)}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * The signed-in frame (WEB-005/006): bottom tabs at phone widths, side
 * navigation from tablet widths up, one `<main>` for the page. The skip link
 * comes first so keyboard users can jump past the navigation.
 */
export function AppShell() {
  const t = useT();
  const compact = useIsCompact();
  const unread = useUnreadMessagesCount();
  const newOffers = useOffersWatcher();
  const isAdmin = useSessionStore((s) => Boolean(s.member?.isAdmin));
  const pageTitle = usePageTitle((s) => s.title);
  // A chat on a phone fills the screen (its composer sits where the tabs would), as on Android.
  const immersive = useMatches().some(
    (match) => (match.handle as { immersive?: boolean } | undefined)?.immersive,
  );
  const bottomNav = compact && !immersive;
  // The live connection's state, for styling and so end-to-end tests can wait until the page is live.
  const realtime = useConnectionStore((s) => s.messaging);
  // A call on screen (not just ringing) covers the app: keep keyboard and screen readers in it.
  const callCovers = useCallStore((s) => s.phase !== 'idle' && s.phase !== 'incoming-ringing');
  const inCall = useCallStore((s) => s.phase !== 'idle');
  const warned = useWarningsStore((s) => selectPending(s).length > 0);

  useEffect(() => {
    const timer = setTimeout(() => void loadCallOverlay(), PREFETCH_CALL_OVERLAY_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const base = pageTitle ? `${pageTitle} · Kindra` : 'Kindra';
    document.title = unread > 0 ? `(${unread}) ${base}` : base;
  }, [unread, pageTitle]);

  return (
    <div
      className={styles.shell}
      data-layout={compact ? 'bottom' : 'side'}
      data-immersive={immersive || undefined}
      data-realtime={realtime}
    >
      <a href="#main" className={styles.skipLink}>
        {t('Skip to content')}
      </a>
      <div className={styles.frame} inert={callCovers || undefined}>
        {compact ? null : (
          <MainNav layout="side" unread={unread} newOffers={newOffers} isAdmin={isAdmin} />
        )}
        <main id="main" tabIndex={-1} className={styles.main}>
          <Outlet />
        </main>
        {bottomNav ? (
          <MainNav layout="bottom" unread={unread} newOffers={newOffers} isAdmin={isAdmin} />
        ) : null}
      </div>
      <Overlays />
      <Suspense fallback={null}>
        {warned ? <WarningNotice /> : null}
        {inCall ? <CallOverlay /> : null}
      </Suspense>
    </div>
  );
}
